import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import {
  ChevronRight,
  LayoutGrid,
  Search,
  ArrowUpDown,
  Layers,
  User,
  Calendar,
  Building2,
  Shield,
} from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { buildInvestigationWorkspaceCasesQuery } from '../../config/investigationWorkspace'
import {
  getWorkspaceStageId,
  resolveStageLabel,
  getWorkspaceStageLabel,
  resolvePatronListTitle,
  resolvePatronListSecondaryId,
  dedupeInvestigationCasesOnePerMemberId,
} from '../../utils/workspaceCaseStage'

const P = {
  pri: '#0a5169',
  priM: '#e8f2f6',
  ink: '#0f172a',
  slate: '#64748b',
  slateL: '#94a3b8',
  border: '#e2e8f0',
  cardBg: '#ffffff',
  pageBg: '#f6f9fb',
  red: '#dc2626',
  amber: '#d97706',
  green: '#16a34a',
  purple: '#7c3aed',
}

const STAGE_GROUP_ORDER = [
  'investigation',
  'pending_evidence',
  'suspicion_formed',
  'smr_draft',
  'under_review',
  'filed',
  'closed',
]

const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest first' },
  { id: 'oldest', label: 'Oldest first' },
  { id: 'progress_high', label: 'Step progress (high → low)' },
  { id: 'progress_low', label: 'Step progress (low → high)' },
  { id: 'steps_remaining', label: 'Most steps remaining' },
  { id: 'steps_remaining_asc', label: 'Fewest steps remaining' },
  { id: 'member_az', label: 'Member A–Z' },
  { id: 'member_za', label: 'Member Z–A' },
  { id: 'severity', label: 'Severity (high first)' },
]

const GROUP_OPTIONS = [
  { id: 'stage', label: 'By pipeline stage' },
  { id: 'case_type', label: 'By case type' },
  { id: 'status', label: 'By status' },
  { id: 'none', label: 'No grouping (flat list)' },
]

function severityRank(s) {
  const u = String(s || '').toUpperCase()
  if (u === 'CRITICAL') return 4
  if (u === 'HIGH') return 3
  if (u === 'MEDIUM') return 2
  if (u === 'LOW') return 1
  return 0
}

function stepsRemaining(c) {
  const t = Number(c.steps_total) || 0
  const d = Number(c.steps_done) || 0
  return Math.max(0, t - d)
}

function sortCases(list, sortBy) {
  const copy = [...list]
  const byDate = (a, b, asc) => {
    const ta = new Date(a.created_at || 0).getTime()
    const tb = new Date(b.created_at || 0).getTime()
    return asc ? ta - tb : tb - ta
  }
  switch (sortBy) {
    case 'newest':
      copy.sort((a, b) => byDate(a, b, false))
      break
    case 'oldest':
      copy.sort((a, b) => byDate(a, b, true))
      break
    case 'progress_high':
      copy.sort((a, b) => (Number(b.progress) || 0) - (Number(a.progress) || 0))
      break
    case 'progress_low':
      copy.sort((a, b) => (Number(a.progress) || 0) - (Number(b.progress) || 0))
      break
    case 'steps_remaining':
      copy.sort((a, b) => stepsRemaining(b) - stepsRemaining(a))
      break
    case 'steps_remaining_asc':
      copy.sort((a, b) => stepsRemaining(a) - stepsRemaining(b))
      break
    case 'member_az':
      copy.sort((a, b) => {
        const na = resolvePatronListTitle(a)
        const nb = resolvePatronListTitle(b)
        const cmp = na.localeCompare(nb, undefined, { sensitivity: 'base' })
        if (cmp !== 0) return cmp
        return String(a.case_number || a.id || '').localeCompare(String(b.case_number || b.id || ''), undefined, {
          sensitivity: 'base',
        })
      })
      break
    case 'member_za':
      copy.sort((a, b) => {
        const na = resolvePatronListTitle(a)
        const nb = resolvePatronListTitle(b)
        const cmp = nb.localeCompare(na, undefined, { sensitivity: 'base' })
        if (cmp !== 0) return cmp
        return String(b.case_number || b.id || '').localeCompare(String(a.case_number || a.id || ''), undefined, {
          sensitivity: 'base',
        })
      })
      break
    case 'severity':
      copy.sort((a, b) => severityRank(b.severity) - severityRank(a.severity))
      break
    default:
      copy.sort((a, b) => byDate(a, b, false))
  }
  return copy
}

/** Stable DOM id for group scroll targets (URL-safe). */
function workspaceGroupAnchorId(groupKey) {
  const raw = String(groupKey || 'group')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `workspace-group-${raw || 'unknown'}`
}

function groupSortedCases(list, groupBy, stageLabelMap = {}) {
  if (groupBy === 'none') {
    return [{ key: '_all', label: 'All cases', items: list }]
  }
  const buckets = new Map()
  for (const c of list) {
    let key
    let label
    if (groupBy === 'case_type') {
      key = String(c.case_type || 'UNKNOWN').toUpperCase()
      label = key
    } else if (groupBy === 'status') {
      key = String(c.status || 'UNKNOWN').toUpperCase()
      label = key.replace(/_/g, ' ')
    } else if (groupBy === 'stage') {
      key = getWorkspaceStageId(c)
      label = resolveStageLabel(key, stageLabelMap)
    } else {
      key = '_all'
      label = 'All'
    }
    if (!buckets.has(key)) buckets.set(key, { key, label, items: [] })
    buckets.get(key).items.push(c)
  }
  const arr = Array.from(buckets.values())
  if (groupBy === 'stage') {
    arr.sort((a, b) => {
      const ia = STAGE_GROUP_ORDER.indexOf(a.key)
      const ib = STAGE_GROUP_ORDER.indexOf(b.key)
      const sa = ia === -1 ? 999 : ia
      const sb = ib === -1 ? 999 : ib
      if (sa !== sb) return sa - sb
      return a.label.localeCompare(b.label)
    })
  } else {
    arr.sort((a, b) => a.label.localeCompare(b.label))
  }
  return arr
}

function CaseCard({ inv, onOpen, stageLabels = {}, disambiguatePatronTitle = false }) {
  const caseId = inv.case_number || inv.id
  const basePatronTitle = resolvePatronListTitle(inv)
  const displayName =
    disambiguatePatronTitle && caseId ? `${basePatronTitle} · ${caseId}` : basePatronTitle
  const subId = resolvePatronListSecondaryId(inv)
  const daysOpen = inv.days_open ?? 0
  const progress = Number(inv.progress) || 0
  const stepsDone = Number(inv.steps_done) || 0
  const stepsTotal = Number(inv.steps_total) || 0
  const remaining = stepsRemaining(inv)
  const stageLabel = getWorkspaceStageLabel(inv, stageLabels)
  const hasSteps = stepsTotal > 0
  const sev = String(inv.severity || '').toUpperCase()
  const sevColor =
    sev === 'CRITICAL' || sev === 'HIGH' ? P.red : sev === 'MEDIUM' ? P.amber : P.slate

  return (
    <button
      type="button"
      onClick={() => onOpen(caseId)}
      className="w-full text-left rounded-xl border transition-all duration-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#0a5169]"
      style={{
        background: P.cardBg,
        borderColor: P.border,
        padding: '16px 18px',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <User size={15} style={{ color: P.pri, flexShrink: 0 }} />
            <span className="font-bold text-[15px] truncate" style={{ color: P.ink }}>
              {displayName}
            </span>
          </div>
          <div className="font-mono text-[12px] mb-2" style={{ color: P.slateL }}>
            {subId ? <span>{subId}</span> : null}
            {subId && caseId ? <span> · </span> : null}
            {caseId ? <span>{caseId}</span> : null}
            {!subId && !caseId ? <span>—</span> : null}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {inv.case_type && (
              <span
                className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md"
                style={{ background: P.priM, color: P.pri }}
              >
                {inv.case_type}
              </span>
            )}
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded-md"
              style={{ background: '#f1f5f9', color: P.slate }}
            >
              {stageLabel}
            </span>
            {inv.status && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-md" style={{ background: '#f8fafc', color: P.slateL }}>
                {String(inv.status).replace(/_/g, ' ')}
              </span>
            )}
            {sev && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-0.5" style={{ color: sevColor, background: `${sevColor}12` }}>
                <Shield size={10} />
                {sev}
              </span>
            )}
          </div>
          {inv.rule_name && (
            <div className="text-[12px] mt-2 truncate" style={{ color: P.slate }}>
              {inv.rule_name}
            </div>
          )}
        </div>
        <ChevronRight size={18} style={{ color: P.slateL, flexShrink: 0, marginTop: 4 }} />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[11px]" style={{ color: P.slateL }}>
        {(inv.venue_name || inv.venue_code) && (
          <span className="inline-flex items-center gap-1">
            <Building2 size={12} />
            {inv.venue_name || inv.venue_code}
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <Calendar size={12} />
          {daysOpen <= 0 ? 'Opened today' : `${daysOpen} day${daysOpen === 1 ? '' : 's'} open`}
        </span>
      </div>

      {hasSteps && (
        <div className="mt-3">
          <div className="flex justify-between text-[11px] mb-1" style={{ color: P.slate }}>
            <span>
              Steps: <strong>{stepsDone}</strong> of <strong>{stepsTotal}</strong>
              {remaining > 0 && <span style={{ color: P.slateL }}> ({remaining} left)</span>}
            </span>
            <span className="font-semibold" style={{ color: P.pri }}>
              {progress}%
            </span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#f1f5f9' }}>
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${progress}%`,
                background: progress >= 80 ? P.green : progress >= 40 ? P.pri : P.amber,
              }}
            />
          </div>
        </div>
      )}
      {!hasSteps && (String(inv.case_type || '').toUpperCase() === 'SUSPICIOUS' || String(inv.case_type || '').toUpperCase() === 'UAR') && (
        <div className="mt-3 text-[11px] italic" style={{ color: P.slateL }}>
          No investigation checklist on this matter — open to review alerts and evidence.
        </div>
      )}
    </button>
  )
}

export default function InvestigationList() {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [investigations, setInvestigations] = useState([])
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('newest')
  const [groupBy, setGroupBy] = useState('stage')
  const [stageLabels, setStageLabels] = useState({})

  useEffect(() => {
    let cancelled = false
    let visTimer = null
    const loadInvestigations = async () => {
      try {
        setLoading(true)
        setError(null)
        const qs = buildInvestigationWorkspaceCasesQuery()
        const res = await authFetch(getLocalApiUrl(`/api/cases?${qs}`))
        const json = await res.json()
        if (cancelled) return
        if (!json.success) throw new Error(json.error || 'Failed to fetch investigations')
        setInvestigations(json.data || [])
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load investigations')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    loadInvestigations()
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || cancelled) return
      clearTimeout(visTimer)
      visTimer = setTimeout(() => {
        if (!cancelled) loadInvestigations()
      }, 400)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      cancelled = true
      clearTimeout(visTimer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [location.key])

  // Same source as Investigation Pipeline: Pipeline → INVESTIGATION_STAGE_LABELS
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/configuration'))
        const json = await res.json()
        if (cancelled || !json.success) return
        const pipeline = (json.data || []).find((c) => c.category === 'Pipeline')
        const setting = pipeline?.settings?.find((s) => s.name === 'INVESTIGATION_STAGE_LABELS')
        if (setting?.value) {
          try {
            const labels = JSON.parse(setting.value)
            if (labels && typeof labels === 'object') setStageLabels(labels)
          } catch {
            /* keep defaults */
          }
        }
      } catch {
        /* keep defaults */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return investigations
    return investigations.filter((c) => {
      const id = `${c.case_number || ''} ${c.id || ''}`.toLowerCase()
      const name = `${c.member_name || ''} ${c.member_id || ''} ${resolvePatronListTitle(c)}`.toLowerCase()
      const rule = `${c.rule_name || ''} ${c.rule_id || ''}`.toLowerCase()
      const venue = `${c.venue_name || ''} ${c.venue_code || ''}`.toLowerCase()
      return id.includes(q) || name.includes(q) || rule.includes(q) || venue.includes(q)
    })
  }, [investigations, search])

  const sorted = useMemo(() => sortCases(filtered, sortBy), [filtered, sortBy])
  const workspaceCases = useMemo(
    () => dedupeInvestigationCasesOnePerMemberId(sorted),
    [sorted]
  )

  /** Same patron title on multiple visible rows — append case id on the title line so cards are distinct. */
  const patronTitleDupKeys = useMemo(() => {
    const counts = new Map()
    for (const inv of workspaceCases) {
      const t = resolvePatronListTitle(inv)
      const k = String(t).trim().toLowerCase() || '\0'
      counts.set(k, (counts.get(k) || 0) + 1)
    }
    const dups = new Set()
    for (const [k, n] of counts.entries()) {
      if (n > 1) dups.add(k)
    }
    return dups
  }, [workspaceCases])

  const grouped = useMemo(() => groupSortedCases(workspaceCases, groupBy, stageLabels), [workspaceCases, groupBy, stageLabels])

  const openCase = (caseId) => navigate(`/compliance-safe/investigations/${encodeURIComponent(caseId)}`)

  const scrollToGroup = useCallback((groupKey) => {
    const id = workspaceGroupAnchorId(groupKey)
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      try {
        history.replaceState(null, '', `#${id}`)
      } catch {
        /* ignore */
      }
    }
  }, [])

  // Deep link: URL hash #workspace-group-… scrolls to that section after render
  useEffect(() => {
    if (loading || workspaceCases.length === 0 || groupBy === 'none') return
    const hash = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : ''
    if (!hash || !hash.startsWith('workspace-group-')) return
    const t = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 120)
    return () => window.clearTimeout(t)
  }, [loading, workspaceCases.length, groupBy])

  const showGroupNav = groupBy !== 'none' && workspaceCases.length > 0

  if (loading) {
    return (
      <div className="min-h-full p-6" style={{ background: P.pageBg }}>
        <div className="max-w-5xl mx-auto rounded-xl border p-10 text-center text-sm" style={{ borderColor: P.border, background: P.cardBg, color: P.slate }}>
          Loading workspace…
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-full p-6" style={{ background: P.pageBg }}>
        <div className="max-w-5xl mx-auto rounded-xl border p-10 text-sm" style={{ borderColor: P.border, background: P.cardBg, color: P.red }}>
          {error}
        </div>
      </div>
    )
  }

  const GroupNavLinks = ({ className = '', compact = false }) => (
    <nav className={className} aria-label="Jump to group">
      <ul className="list-none m-0 p-0 space-y-0.5">
        {grouped.map((group) => {
          const anchor = workspaceGroupAnchorId(group.key)
          return (
            <li key={group.key}>
              <button
                type="button"
                onClick={() => scrollToGroup(group.key)}
                className={`w-full text-left rounded-lg border border-transparent transition-colors hover:bg-white/80 ${compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'}`}
                style={{ color: P.ink }}
              >
                <span className="font-semibold block truncate" title={group.label}>
                  {group.label}
                </span>
                <span className="text-[11px] font-bold tabular-nums" style={{ color: P.slateL }}>
                  {group.items.length} case{group.items.length === 1 ? '' : 's'}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )

  return (
    <div className="min-h-full p-6" style={{ background: P.pageBg }}>
      <div className={`mx-auto space-y-5 ${showGroupNav ? 'max-w-6xl xl:max-w-7xl' : 'max-w-5xl'}`}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-extrabold tracking-tight m-0" style={{ color: P.ink }}>
              Investigation workspace
            </h1>
            <p className="text-sm m-0 mt-1.5 leading-relaxed" style={{ color: P.slate }}>
              Same cases as the pipeline, grouped by stage with quick navigation. Stage names follow your Pipeline settings. Sort or change grouping below
              if you need a different view.
            </p>
          </div>
          <Link
            to="/compliance-safe/investigations/pipeline"
            className="inline-flex items-center gap-2 shrink-0 text-sm font-bold px-4 py-2.5 rounded-lg border no-underline transition-colors hover:opacity-90"
            style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
          >
            <LayoutGrid size={16} />
            Pipeline view
          </Link>
        </div>

        <div className="rounded-xl border p-4 sm:p-5 space-y-4" style={{ borderColor: P.border, background: P.cardBg }}>
          <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
            <div className="flex-1 min-w-0">
              <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color: P.slateL }}>
                Search
              </label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: P.slateL }} />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Member, case number, rule, venue…"
                  className="w-full rounded-lg border pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2"
                  style={{ borderColor: P.border, color: P.ink }}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color: P.slateL }}>
                  <span className="inline-flex items-center gap-1">
                    <ArrowUpDown size={11} /> Sort
                  </span>
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-lg border px-3 py-2.5 text-sm min-w-[200px] cursor-pointer bg-white"
                  style={{ borderColor: P.border, color: P.ink }}
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color: P.slateL }}>
                  <span className="inline-flex items-center gap-1">
                    <Layers size={11} /> Group by
                  </span>
                </label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="rounded-lg border px-3 py-2.5 text-sm min-w-[200px] cursor-pointer bg-white"
                  style={{ borderColor: P.border, color: P.ink }}
                >
                  {GROUP_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="text-xs font-semibold" style={{ color: P.slate }}>
            Showing <strong style={{ color: P.ink }}>{workspaceCases.length}</strong>
            {filtered.length !== investigations.length && (
              <>
                {' '}
                of <strong style={{ color: P.ink }}>{investigations.length}</strong>
              </>
            )}{' '}
            case{workspaceCases.length === 1 ? '' : 's'}
          </div>
        </div>

        {workspaceCases.length === 0 ? (
          <div className="rounded-xl border p-12 text-center text-sm" style={{ borderColor: P.border, background: P.cardBg, color: P.slate }}>
            {investigations.length === 0
              ? 'No cases in this workspace yet. New matters appear here when rules create pipeline cases.'
              : 'No cases match your search. Try different keywords or clear the search box.'}
          </div>
        ) : (
          <div className={`flex flex-col gap-6 ${showGroupNav ? 'lg:flex-row lg:items-start lg:gap-8' : ''}`}>
            {showGroupNav && (
              <>
                <aside
                  className="hidden lg:block shrink-0 w-52 xl:w-56 sticky top-4 z-10 rounded-xl border p-3"
                  style={{ borderColor: P.border, background: P.cardBg, maxHeight: 'calc(100vh - 5rem)' }}
                >
                  <div className="text-[10px] font-extrabold uppercase tracking-wider mb-2 px-1" style={{ color: P.slateL }}>
                    Jump to group
                  </div>
                  <div className="overflow-y-auto pr-1" style={{ maxHeight: 'calc(100vh - 8rem)' }}>
                    <GroupNavLinks />
                  </div>
                </aside>
                <div
                  className="lg:hidden -mx-1 px-1 pb-1 overflow-x-auto flex gap-2 snap-x snap-mandatory"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {grouped.map((group) => (
                    <button
                      key={group.key}
                      type="button"
                      onClick={() => scrollToGroup(group.key)}
                      className="snap-start shrink-0 rounded-full border px-3 py-2 text-xs font-bold whitespace-nowrap"
                      style={{ borderColor: P.border, background: P.priM, color: P.pri }}
                    >
                      {group.label}
                      <span className="opacity-80 font-semibold ml-1">({group.items.length})</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="flex-1 min-w-0 space-y-8">
              {grouped.map((group) => (
                <section
                  key={group.key}
                  id={groupBy !== 'none' ? workspaceGroupAnchorId(group.key) : undefined}
                  className="scroll-mt-24"
                >
                  {groupBy !== 'none' && (
                    <h2 className="text-base font-extrabold tracking-tight m-0 mb-3 flex items-center gap-2" style={{ color: P.pri }}>
                      <span>{group.label}</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: P.priM, color: P.pri }}>
                        {group.items.length}
                      </span>
                    </h2>
                  )}
                  <ul className="list-none m-0 p-0 space-y-3">
                    {group.items.map((inv) => {
                      const baseTitle = resolvePatronListTitle(inv)
                      const dupKey = String(baseTitle).trim().toLowerCase() || '\0'
                      return (
                        <li key={inv.case_number || inv.id}>
                          <CaseCard
                            inv={inv}
                            onOpen={openCase}
                            stageLabels={stageLabels}
                            disambiguatePatronTitle={patronTitleDupKeys.has(dupKey)}
                          />
                        </li>
                      )
                    })}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
